import {useContext, useState, useEffect} from 'react'
import {ProductReportContext} from '../../context'
import Search from '../../../../components/Search'
import Select from '../../../../components/Select/Select'
import { ConfirmationModal } from '../../../../components/ConfirmationModal'
import Import from '../../../../images/Import.png'

export const DAYS_OPTIONS = [
  {label: 'Days: 07', value: 7},
  {label: 'Days: 15', value: 15},
  {label: 'Days: 30', value: 30},
  {label: 'Days: 45', value: 45},
  {label: 'Days: 60', value: 60},
  {label: 'Days: 90', value: 90},
  {label: 'Days: 180', value: 180},
]

const ProductReportHeader = () => {
  const {
    filters,
    onSearch,
    onDaysChange,
    onPurchaserChange,
    onHideDiscontinuedChange,
    purchasers,
    isPurchasersLoading,
    isLoading,
    reportData,
    onDownloadCsv,
    isFetchingCsvData,
    pagination,
  } = useContext(ProductReportContext)
  const [showModal, setShowModal] = useState(false)
  const [message, setMessage] = useState('')

  // Local state for filters
  const [localFilters, setLocalFilters] = useState({
    days: filters.days_to_replenishment,
    purchaser: filters.purchaser,
    hide_discontinued: filters.hide_discontinued,
  })

  // Update local filters when context filters change
  useEffect(() => {
    setLocalFilters({
      days: filters.days_to_replenishment,
      purchaser: filters.purchaser,
      hide_discontinued: filters.hide_discontinued,
    })
  }, [filters])

  const handleDaysChange = (value: string) => {
    setLocalFilters((prev) => ({...prev, days: value}))
  }

  const handlePurchaserChange = (value: string) => {
    setLocalFilters((prev) => ({...prev, purchaser: value}))
  }

  const handleHideDiscontinuedChange = (checked: boolean) => {
    setLocalFilters((prev) => ({...prev, hide_discontinued: checked}))
  }

  const onApplyFilter = () => {
    onDaysChange(localFilters.days)
    onPurchaserChange(localFilters.purchaser)
    onHideDiscontinuedChange(localFilters.hide_discontinued)
  }

  const onResetFilters = () => {
    setLocalFilters({
      days: '180',
      purchaser: '',
      hide_discontinued: true,
    })
    onDaysChange('180')
    onPurchaserChange('')
    onHideDiscontinuedChange(true)
  }

  async function downloadCsvFile() {
    const res: any = await onDownloadCsv()
    if (res?.status === 200) {
      setShowModal(true)
      setMessage(res?.data?.message)
    }
  }

  const isFiltersDirty = () => {
    return (
      filters.days_to_replenishment !== '180' ||
      filters.purchaser !== '' ||
      filters.hide_discontinued !== true
    )
  }

  return (
    <div>
      <div className='mb-5'>
        <div className='d-flex flex-wrap align-items-center mb-8'>
          <Search onSearch={onSearch} placeholder='Search products' />
        </div>
        <div className='d-flex justify-content-between'>
          <div className='d-flex align-items-center gap-5'>
            <Select
              id='days-filter'
              label='Days'
              options={DAYS_OPTIONS}
              className='w-120px'
              value={localFilters.days}
              onChange={(e: any) => handleDaysChange(e.target.value)}
            />

            <Select
              id='purchaser-filter'
              label='Purchaser'
              options={purchasers}
              className='w-175px'
              placeholder='Select Purchaser'
              isNullable={true}
              valueKey='label'
              isLoading={isPurchasersLoading}
              value={localFilters.purchaser}
              onChange={(e: any) => handlePurchaserChange(e.target.value)}
            />

            <div className='form-check mt-8'>
              <input
                className='form-check-input'
                type='checkbox'
                id='hide-discontinued'
                checked={localFilters.hide_discontinued}
                onChange={(e) => handleHideDiscontinuedChange(e.target.checked)}
              />
              <label className='form-check-label ms-2' htmlFor='hide-discontinued'>
                Hide Discontinued Products
              </label>
            </div>

            <div className='d-flex align-items-center gap-5 mt-8'>
              <button className='btn btn-primary ms-5' onClick={onApplyFilter} disabled={isLoading}>
                Apply Filters
              </button>
              {isFiltersDirty() ? (
                <button
                  type='button'
                  className='btn btn-outline'
                  onClick={onResetFilters}
                  disabled={isLoading}
                >
                  Reset Filters
                </button>
              ) : null}
            </div>
          </div>
          <div className='mt-8'>
            {reportData && reportData.length > 0 ? (
              <button
                className='btn btn-outline'
                onClick={downloadCsvFile}
                disabled={isFetchingCsvData}
              >
                <div className='d-flex align-items-center'>
                  <img src={Import} alt='Import File' className='me-2 import-icon' />
                  <div className='d-flex align-items-center'>
                    {isFetchingCsvData ? 'Downloading...' : 'Export CSV'}
                    {isFetchingCsvData && (
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    )}
                  </div>
                </div>
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {reportData?.length > 0 ? (
        <div className='mb-10'>
          <p className='text-inderline d-flex align-items-center'>
            <span className='fw-semibold'>Total</span>
            <span className='fw-bolder fs-3 me-2 ms-2'>{pagination.total}</span>
            <span className='fw-semibold'>Products</span>
          </p>
        </div>
      ) : null}
      <ConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false)
          setMessage('')
        }}
        title={'Generating Your CSV File'}
        disableAction={true}
        onAction={() => {
          setShowModal(false)
          setMessage('')
        }}
        okayBtnClass={'btn-primary'}
        body={
          <div>
            <p>We are generating the data you requested for download as a CSV file.</p>
            <p>
              This process may take a few minutes. Once complete, the CSV will be sent to your
              email: <b>{message.split(':')[1]}</b>
            </p>
          </div>
        }
      />
    </div>
  )
}

export default ProductReportHeader
